import React, { useState } from 'react';
import { Lightbulb, Loader2, AlertCircle } from 'lucide-react';
import useCoaching from '../../hooks/useCoaching';
import { isV4 } from '../../lib/uiFlag';

// Copilot coaching v1 (2026-09-14) — a DEDICATED, READ-ONLY panel, separate
// from the copilot Q&A box below it (Copilot.jsx is untouched). It shows the
// latest one-sentence coaching cue fired by one of the two named triggers
// (lib/coachingTriggers.js) and up to three earlier cues, dimmed. No accept,
// no dismiss, no feedback control — those are explicitly deferred in
// docs/ops/copilot-coaching-scoping-2026-09-14.md.
//
// Idle state is a quiet one-liner rather than nothing, so an agent knows the
// panel exists before the first cue lands. If the endpoint is not configured
// the panel renders nothing at all.
export default function CoachingPanel() {
  const { latest, history, pending, error, enabled } = useCoaching();
  const [showEarlier, setShowEarlier] = useState(false);
  const v4 = isV4();
  if (!enabled) return null;

  return (
    <div className="rr-section coaching" aria-live="polite">
      <div className="rr-section-title coaching-title">
        <Lightbulb size={12} />
        Coaching
        {pending && <Loader2 size={11} className="spin coaching-spin" />}
      </div>

      {error && (
        <div className="coaching-error">
          <AlertCircle size={12} />
          <span>{error}</span>
        </div>
      )}

      {latest ? (
        <div className="coaching-cue">
          <div className="coaching-cue-head">
            <span className="coaching-cue-label">{latest.label}</span>
            <span className="coaching-cue-time">{fmtTime(latest.at)}</span>
          </div>
          <div className="coaching-cue-text">{latest.suggestion}</div>
        </div>
      ) : (
        !error && <p className="panel-empty coaching-idle">Watching the call — cues appear here when needed.</p>
      )}

      {/* Console v4 (row 234): earlier cues fold behind one link, so only the
          newest cue competes for attention. v3 shows them as before. */}
      {v4 && history.length > 0 && (
        <button type="button" className="v4-link" aria-expanded={showEarlier} onClick={() => setShowEarlier(s => !s)}>
          {showEarlier ? 'Hide earlier cues' : `${history.length} earlier cue${history.length === 1 ? '' : 's'}`}
        </button>
      )}
      {history.length > 0 && (!v4 || showEarlier) && (
        <div className="coaching-history">
          {history.map((h, i) => (
            <div key={`${h.at}-${i}`} className="coaching-history-item">
              <span className="coaching-cue-time">{fmtTime(h.at)}</span>
              <span className="coaching-history-text">{h.suggestion}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function fmtTime(ts) {
  try {
    return new Date(ts).toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false });
  } catch { return ''; }
}
